import { useEffect, useState } from 'react';
import { FamConstituents, WrappersService } from 'shared/services/WrappersService';
import { createLogger } from 'shared/logger';

const log = createLogger('FamFofExpansion');

// FAM FoF constituents widget (issue #90). The parent-ISIN list and each
// fund's constituents change once a month (FAM feed), so both are cached for
// the page lifetime, shared across every composition table on screen.
let parentsPromise: Promise<Set<string>> | null = null;
const constituentsCache = new Map<string, FamConstituents>();

const loadParents = (): Promise<Set<string>> => {
  if (!parentsPromise) {
    parentsPromise = WrappersService.getFamParents()
      .then((r) => new Set(r.isins))
      .catch((err) => {
        log.warn('fam parents fetch failed', { error: err });
        parentsPromise = null; // allow a retry on next mount
        return new Set<string>();
      });
  }
  return parentsPromise;
};

export type FamExpansionState = FamConstituents | 'loading' | 'error';

export const useFamFofExpansion = () => {
  const [famParents, setFamParents] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Record<string, FamExpansionState>>({});

  useEffect(() => {
    let alive = true;
    loadParents().then((isins) => {
      if (alive) setFamParents(isins);
    });
    return () => {
      alive = false;
    };
  }, []);

  const toggle = (isin: string) => {
    setExpanded((prev) => {
      // Any open state (loaded, loading, error) collapses; a failed fetch is
      // retried by expanding again — errors are never cached.
      if (prev[isin]) {
        const { [isin]: _closed, ...rest } = prev;
        return rest;
      }
      const cached = constituentsCache.get(isin);
      if (cached) return { ...prev, [isin]: cached };
      WrappersService.getFamConstituents(isin)
        .then((data) => {
          constituentsCache.set(isin, data);
          setExpanded((p) => (p[isin] ? { ...p, [isin]: data } : p));
        })
        .catch((err) => {
          log.warn('fam constituents fetch failed', { isin, error: err });
          setExpanded((p) => (p[isin] ? { ...p, [isin]: 'error' } : p));
        });
      return { ...prev, [isin]: 'loading' };
    });
  };

  return { famParents, expanded, toggle };
};
